import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Loader2, UserMinus, Calendar, MessageSquare, CheckCircle2, XCircle,
    ClipboardList, BookOpen, AlertCircle,
} from "lucide-react";
import { api } from "../../lib/api";

/**
 * V25 Phase 4 — Lead Lifecycle Panel (admin)
 * Displayed inside LeadDetailModal (for `kind === "registrations"`).
 *
 * Shows:
 *  - Currently assigned tutor (if any) — with Cancel / Reschedule / Reassign
 *  - Tutor's remarks + Parent feedback capture
 *  - Approve & Start Tuition button (creates ongoing_tuitions record)
 *  - Internal admin-only notes
 *  - Full assignment history
 */
export default function LeadLifecyclePanel({ leadId, lead, onChanged }) {
    const [loading, setLoading] = useState(true);
    const [history, setHistory] = useState([]);
    const [adminNotes, setAdminNotes] = useState([]);
    const [busy, setBusy] = useState("");
    const [showApprove, setShowApprove] = useState(false);
    const [showFeedback, setShowFeedback] = useState(false);
    const [showReschedule, setShowReschedule] = useState(false);
    const [noteText, setNoteText] = useState("");

    const reload = async () => {
        setLoading(true);
        try {
            const [hist, notes] = await Promise.all([
                api.get(`/admin/leads/${leadId}/assignment-history`),
                api.get(`/admin/leads/${leadId}/admin-notes`),
            ]);
            setHistory(hist.data.items || []);
            setAdminNotes(notes.data.items || []);
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message);
        } finally { setLoading(false); }
    };

    useEffect(() => { reload(); /* eslint-disable-next-line */ }, [leadId]);

    const active = history.find((h) => ["assigned", "demo_scheduled", "demo_done"].includes(h.status));

    const cancelAssignment = async () => {
        if (!window.confirm("Cancel current assignment? The lead will return to the pool for other tutors.")) return;
        setBusy("cancel");
        try {
            await api.post(`/admin/leads/${leadId}/cancel-assignment`, { reason: "admin_cancelled" });
            toast.success("Assignment cancelled");
            await reload();
            onChanged?.();
        } catch (err) { toast.error(err.response?.data?.detail || err.message); }
        finally { setBusy(""); }
    };

    const addNote = async () => {
        if (!noteText.trim()) return;
        setBusy("note");
        try {
            await api.post(`/admin/leads/${leadId}/admin-note`, { note: noteText.trim() });
            setNoteText("");
            await reload();
        } catch (err) { toast.error(err.response?.data?.detail || err.message); }
        finally { setBusy(""); }
    };

    if (loading) return <div className="p-3 text-center text-slate-400 text-sm"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading lifecycle…</div>;

    return (
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-4" data-testid="lead-lifecycle-panel">
            <div className="flex items-center justify-between">
                <h3 className="font-semibold text-[#0F172A] text-sm flex items-center gap-2">
                    <ClipboardList className="w-4 h-4 text-slate-500" /> Demo Lifecycle
                </h3>
                {lead?.ongoing_tuition_id && (
                    <span className="inline-flex items-center gap-1 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5">
                        <BookOpen className="w-3 h-3" /> Ongoing tuition active
                    </span>
                )}
            </div>

            {/* Active assignment */}
            {active ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2" data-testid="active-assignment-card">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-[10px] uppercase tracking-wider font-bold text-blue-700">{active.status.replace(/_/g, " ")}</div>
                            <div className="font-semibold text-slate-800">{active.tutor_name}</div>
                            <div className="text-xs text-slate-500">{active.tutor_email} · {active.tutor_phone}</div>
                            {active.demo_scheduled_at && (
                                <div className="text-xs text-slate-600 mt-1"><Calendar className="w-3 h-3 inline mr-1" /> Demo at {active.demo_scheduled_at}</div>
                            )}
                            {active.tutor_remarks && (
                                <div className="text-xs text-slate-700 mt-1 bg-white border border-slate-200 rounded p-2">
                                    <strong>Tutor remarks:</strong> {active.tutor_remarks}
                                </div>
                            )}
                            {active.parent_feedback && (
                                <div className={`text-xs mt-1 rounded p-2 border ${active.parent_feedback.decision === "approve" ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-rose-50 border-rose-200 text-rose-800"}`}>
                                    <strong>Parent feedback:</strong> {active.parent_feedback.decision} {active.parent_feedback.notes ? `— ${active.parent_feedback.notes}` : ""}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-2 pt-2 border-t border-blue-200">
                        <button onClick={() => setShowReschedule(true)} data-testid="lifecycle-reschedule-btn"
                            className="text-xs font-semibold bg-white hover:bg-slate-50 border border-slate-200 px-2.5 py-1 rounded inline-flex items-center gap-1">
                            <Calendar className="w-3 h-3" /> Reschedule demo
                        </button>
                        {active.status === "demo_done" && !active.parent_feedback && (
                            <button onClick={() => setShowFeedback(true)} data-testid="lifecycle-feedback-btn"
                                className="text-xs font-semibold bg-amber-500 hover:bg-amber-600 text-white px-2.5 py-1 rounded inline-flex items-center gap-1">
                                <MessageSquare className="w-3 h-3" /> Record parent feedback
                            </button>
                        )}
                        {active.status === "demo_done" && active.parent_feedback?.decision === "approve" && (
                            <button onClick={() => setShowApprove(true)} data-testid="lifecycle-approve-btn"
                                className="text-xs font-semibold bg-emerald-500 hover:bg-emerald-600 text-white px-2.5 py-1 rounded inline-flex items-center gap-1">
                                <CheckCircle2 className="w-3 h-3" /> Approve &amp; Start Tuition
                            </button>
                        )}
                        <button onClick={cancelAssignment} disabled={busy === "cancel"} data-testid="lifecycle-cancel-btn"
                            className="text-xs font-semibold bg-white hover:bg-rose-50 border border-rose-200 text-rose-700 px-2.5 py-1 rounded inline-flex items-center gap-1 disabled:opacity-60">
                            {busy === "cancel" ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserMinus className="w-3 h-3" />} Cancel assignment
                        </button>
                    </div>
                </div>
            ) : (
                <div className="bg-slate-50 border border-dashed border-slate-300 rounded p-3 text-xs text-slate-600">
                    <AlertCircle className="w-3 h-3 inline mr-1 text-slate-400" />
                    {lead?.pipeline_stage === "demo"
                        ? `No active assignment. Assign a tutor from the "Recommended Tutors" section below to begin the demo lifecycle.`
                        : `No active assignment. Move this lead to "Demo Posting" (Lead Pipeline) to see AI-matched tutor recommendations.`}
                </div>
            )}

            {/* Admin-only internal notes */}
            <div className="border-t border-slate-100 pt-3">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Internal Admin Notes (private)</div>
                <div className="flex gap-2">
                    <input value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add a note (visible only to admin)"
                        data-testid="lifecycle-admin-note-input"
                        className="flex-1 border border-slate-300 rounded-lg px-3 py-1.5 text-sm" />
                    <button onClick={addNote} disabled={busy === "note" || !noteText.trim()} data-testid="lifecycle-admin-note-add"
                        className="px-3 py-1.5 bg-[#1E3A8A] text-white text-xs font-semibold rounded-lg hover:bg-[#1E40AF] disabled:opacity-60">
                        {busy === "note" ? <Loader2 className="w-3 h-3 animate-spin" /> : "Add"}
                    </button>
                </div>
                {adminNotes.length > 0 && (
                    <div className="mt-2 space-y-1.5">
                        {adminNotes.slice(0, 5).map((n) => (
                            <div key={n.id} className="text-xs bg-amber-50 border border-amber-200 rounded p-2">
                                <div className="text-[10px] text-amber-700 mb-0.5">{n.by} · {new Date(n.at).toLocaleString()}</div>
                                <div className="text-slate-700">{n.note}</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Full history timeline */}
            {history.length > 0 && (
                <div className="border-t border-slate-100 pt-3">
                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Assignment history ({history.length})</div>
                    <div className="space-y-1.5 max-h-40 overflow-y-auto">
                        {history.map((h) => (
                            <div key={h.id} className="text-xs bg-slate-50 border border-slate-200 rounded p-2">
                                <div className="flex items-center justify-between">
                                    <strong className="text-slate-700">{h.tutor_name}</strong>
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${h.status === "approved" ? "bg-emerald-100 text-emerald-700" : h.status === "reassigned" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{h.status}</span>
                                </div>
                                <div className="text-[10px] text-slate-500">Assigned {new Date(h.assigned_at).toLocaleString()} by {h.assigned_by}</div>
                                {h.ended_reason && <div className="text-[10px] text-slate-500">Ended: {h.ended_reason}</div>}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {showApprove && <ApproveModal leadId={leadId} onClose={() => setShowApprove(false)} onDone={() => { setShowApprove(false); reload(); onChanged?.(); }} />}
            {showFeedback && <FeedbackModal leadId={leadId} onClose={() => setShowFeedback(false)} onDone={() => { setShowFeedback(false); reload(); onChanged?.(); }} />}
            {showReschedule && <RescheduleModal leadId={leadId} current={active?.demo_scheduled_at} onClose={() => setShowReschedule(false)} onDone={() => { setShowReschedule(false); reload(); onChanged?.(); }} />}
        </div>
    );
}

function ApproveModal({ leadId, onClose, onDone }) {
    const [form, setForm] = useState({ monthly_fee: "", fee_type: "monthly", schedule: "", start_date: "", notes: "" });
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        if (!form.monthly_fee || !form.schedule || !form.start_date) return toast.error("Fee, schedule and start date are required");
        setBusy(true);
        try {
            await api.post(`/admin/leads/${leadId}/approve-tuition`, {
                monthly_fee: parseFloat(form.monthly_fee),
                fee_type: form.fee_type,
                schedule: form.schedule,
                start_date: form.start_date,
                notes: form.notes || null,
            });
            toast.success("Ongoing tuition created");
            onDone();
        } catch (err) { toast.error(err.response?.data?.detail || err.message); }
        finally { setBusy(false); }
    };
    return (
        <ModalShell title="Approve & Start Tuition" onClose={onClose} testid="approve-tuition-modal">
            <div className="space-y-3">
                <Field label="Monthly fee (₹)" required>
                    <input type="number" value={form.monthly_fee} onChange={(e) => setForm({ ...form, monthly_fee: e.target.value })} data-testid="approve-fee" className={inputCls} placeholder="8000" />
                </Field>
                <Field label="Fee type" required>
                    <select value={form.fee_type} onChange={(e) => setForm({ ...form, fee_type: e.target.value })} data-testid="approve-fee-type" className={inputCls}>
                        <option value="monthly">Monthly</option>
                        <option value="session_wise">Per session</option>
                        <option value="full_course">Full course</option>
                    </select>
                </Field>
                <Field label="Schedule" required>
                    <input value={form.schedule} onChange={(e) => setForm({ ...form, schedule: e.target.value })} data-testid="approve-schedule" className={inputCls} placeholder="Mon, Wed, Fri 5–6 PM" />
                </Field>
                <Field label="Start date" required>
                    <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="approve-start-date" className={inputCls} />
                </Field>
                <Field label="Notes (optional)">
                    <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="approve-notes" className={inputCls} />
                </Field>
                <div className="flex gap-2 pt-2">
                    <button onClick={onClose} className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm">Cancel</button>
                    <button onClick={submit} disabled={busy} data-testid="approve-submit-btn"
                        className="flex-1 px-3 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-sm font-semibold inline-flex items-center justify-center gap-1 disabled:opacity-60">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Start Tuition
                    </button>
                </div>
            </div>
        </ModalShell>
    );
}

function FeedbackModal({ leadId, onClose, onDone }) {
    const [decision, setDecision] = useState("approve");
    const [notes, setNotes] = useState("");
    const [rating, setRating] = useState(5);
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        setBusy(true);
        try {
            await api.post(`/admin/leads/${leadId}/parent-feedback`, { decision, notes: notes || null, rating });
            toast.success("Parent feedback recorded");
            onDone();
        } catch (err) { toast.error(err.response?.data?.detail || err.message); }
        finally { setBusy(false); }
    };
    return (
        <ModalShell title="Record parent feedback" onClose={onClose} testid="feedback-modal">
            <div className="space-y-3">
                <Field label="Decision" required>
                    <select value={decision} onChange={(e) => setDecision(e.target.value)} data-testid="feedback-decision" className={inputCls}>
                        <option value="approve">Approve — Start tuition</option>
                        <option value="need_another_tutor">Wants another tutor</option>
                        <option value="reject">Rejected</option>
                    </select>
                </Field>
                <Field label="Rating (1–5)">
                    <input type="number" min="1" max="5" value={rating} onChange={(e) => setRating(parseInt(e.target.value || "5", 10))} data-testid="feedback-rating" className={inputCls} />
                </Field>
                <Field label="Notes / parent's comments">
                    <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} data-testid="feedback-notes" className={inputCls} placeholder="What did the parent say?" />
                </Field>
                <div className="flex gap-2 pt-2">
                    <button onClick={onClose} className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm">Cancel</button>
                    <button onClick={submit} disabled={busy} data-testid="feedback-submit-btn"
                        className="flex-1 px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-semibold inline-flex items-center justify-center gap-1 disabled:opacity-60">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquare className="w-4 h-4" />} Save feedback
                    </button>
                </div>
            </div>
        </ModalShell>
    );
}

function RescheduleModal({ leadId, current, onClose, onDone }) {
    const [newAt, setNewAt] = useState(current || "");
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        if (!newAt) return toast.error("Pick new date/time");
        setBusy(true);
        try {
            await api.post(`/admin/leads/${leadId}/reschedule-demo`, { new_scheduled_at: newAt, reason: reason || null });
            toast.success("Demo rescheduled");
            onDone();
        } catch (err) { toast.error(err.response?.data?.detail || err.message); }
        finally { setBusy(false); }
    };
    return (
        <ModalShell title="Reschedule demo" onClose={onClose} testid="reschedule-modal">
            <div className="space-y-3">
                <Field label="New date &amp; time" required>
                    <input type="datetime-local" value={newAt} onChange={(e) => setNewAt(e.target.value)} data-testid="reschedule-at" className={inputCls} />
                </Field>
                <Field label="Reason (optional)">
                    <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reschedule-reason" className={inputCls} placeholder="Parent requested" />
                </Field>
                <div className="flex gap-2 pt-2">
                    <button onClick={onClose} className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm">Cancel</button>
                    <button onClick={submit} disabled={busy} data-testid="reschedule-submit-btn"
                        className="flex-1 px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-semibold inline-flex items-center justify-center gap-1 disabled:opacity-60">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calendar className="w-4 h-4" />} Save
                    </button>
                </div>
            </div>
        </ModalShell>
    );
}

const inputCls = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm";

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="text-xs font-semibold text-slate-700 mb-1 block">{label}{required && <span className="text-rose-500">*</span>}</span>
            {children}
        </label>
    );
}

function ModalShell({ title, onClose, testid, children }) {
    return (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center px-4" data-testid={testid}>
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
                <div className="bg-[#1E3A8A] text-white p-4 flex items-center justify-between">
                    <h3 className="font-bold text-base">{title}</h3>
                    <button onClick={onClose} className="text-white/70 hover:text-white"><XCircle className="w-5 h-5" /></button>
                </div>
                <div className="p-4">{children}</div>
            </div>
        </div>
    );
}
